class StickyHeader extends HTMLElement {
	constructor() {
		super();

		this.selectors = {
			headerWrapper: ".header-wrapper",
			headerTopLinks: ".top-bar",
			bottomBar: ".announcement-bottom-bar-container"
		};

		// 滚动方向的判定阈值(px)，低于它视为抖动
		this.scrollThreshold = 4;

		this.headerCssVariables = {
               headerHeight: '--header-height',
			   headerMobileHeight: '--header-height--mobile',
			   topBarHeight: '--top-bar-height',
			   announcementBarHeight: '--announcement-bar-height'
		};

		this.modifiers = {
			hiddenTopBar: "header--top-bar-hidden",
			hiddenAnnouncement:'header--announcement-hidden',
			isHeaderSimplified: "is-header-simplified"
		};

		this.onScrollHandler = this.onScroll.bind(this);
		this.onResizeHandler = this.onResize.bind(this);
	}

	connectedCallback() {

		this.header = this;
		this.currentScrollTop = 0;
		this.headerWrapper = this.header.querySelector(this.selectors.headerWrapper);
		this.headerTopBar = this.header.querySelector(this.selectors.headerTopLinks);
		this.headerBottomBar = this.header.querySelector(this.selectors.bottomBar);
		this.headerHeight = 0;

		this.cssVarCache = {};
		this.resizeFrame = null;
		this.scrollFrame = null;
		this.barsHidden = false;
		this.announcementHidden = false;

		this.attachListeners();

		// 同步测一次，先给出可用的值。theme.liquid 里 body 的
		// padding-top: var(--header-height--mobile) 没有 fallback，
		// 变量未定义时移动端首屏会被 fixed header 盖住。
		this.measure();

		this.observeSize();
	}

	// 不再靠固定延时赌「样式加载完了没有」：异步 CSS 落地、Gotham Rounded
	// 字体替换、logo 图片解码、公告栏 swiper 初始化，任何一项改变了 header
	// 的尺寸都会被观察到并立即重测。
	observeSize() {
		if (typeof ResizeObserver === "undefined") {
			// 降级：保留一次延时补测，至少覆盖晚到的异步 CSS 与字体
			setTimeout(this.onResizeHandler, 2000);
			return;
		}

		this.resizeObserver = new ResizeObserver(this.onResizeHandler);

		[this.header, this.headerWrapper, this.headerTopBar, this.headerBottomBar]
			.filter(Boolean)
			.forEach((element) => this.resizeObserver.observe(element));
	}

	disconnectedCallback() {
		window.removeEventListener("scroll", this.onScrollHandler, false);
		window.removeEventListener("resize", this.onResizeHandler, false);

		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}

		if (this.resizeFrame) {
			cancelAnimationFrame(this.resizeFrame);
			this.resizeFrame = null;
		}

		if (this.scrollFrame) {
			cancelAnimationFrame(this.scrollFrame);
			this.scrollFrame = null;
		}
	}

	// passive 明确告知浏览器这两个回调不会 preventDefault，
	// 滚动合成无需先等 JS 执行完
	attachListeners() {
		window.addEventListener("scroll", this.onScrollHandler, { passive: true });
		window.addEventListener("resize", this.onResizeHandler, { passive: true });
	}

	// ResizeObserver 与 window resize 可能在同一帧内多次触发；合并到下一帧
	// 统一测量。在 ResizeObserver 回调里延到下一帧写样式，也是避免
	// "ResizeObserver loop" 警告的推荐做法。
	onResize() {
		if (this.resizeFrame) return;

		this.resizeFrame = requestAnimationFrame(() => {
			this.resizeFrame = null;
			this.measure();
		});
	}

	measure() {
		this.headerHeight = Math.min(this.header.getBoundingClientRect().height, 141);

		this.setCssVariable(
			this.headerCssVariables.headerHeight,
			`${this.headerHeight}px`
		);

		this.setCssVariable(
			this.headerCssVariables.headerMobileHeight,
			`${this.headerHeight}px`
		);

		if (this.headerWrapper) {
			this.setCssVariable(
				"--header-wrapper-height",
				`${this.headerWrapper.getBoundingClientRect().height}px`
			);
		}

		if (this.headerTopBar) {
			this.setCssVariable(
				"--header-top-bar-height",
				`${this.headerTopBar.getBoundingClientRect().height}px`
			);
		}

		if (this.headerBottomBar) {
			this.setCssVariable(
				"--header-announcement-height",
				`${this.headerBottomBar.getBoundingClientRect().height}px`
			);
		}
	}

	// 值没变就不写，避免每帧都让整棵树的样式失效
	setCssVariable(name, value) {
		if (this.cssVarCache[name] === value) return;

		this.cssVarCache[name] = value;
		document.documentElement.style.setProperty(name, value);
	}

	// 同一帧内可能收到多次 scroll 事件，合并成一次处理。
	// 原先每次事件都 .bind(this) 新建函数并各排一个 rAF，快滚时一帧会堆叠多个回调。
	onScroll() {
		if (this.scrollFrame) return;

		this.scrollFrame = requestAnimationFrame(() => {
			this.scrollFrame = null;
			this.updateBars();
		});
	}

	updateBars() {
		const scrollTop = window.scrollY || document.documentElement.scrollTop;

		if (scrollTop > this.headerHeight) {
			this.toggleAnnouncementBar(true);

			const delta = scrollTop - this.currentScrollTop;

			// 忽略微小位移，否则滚动停顿处的 1px 抖动会让顶栏反复收放
			if (Math.abs(delta) > this.scrollThreshold) {
				this.toggleBars(delta > 0);
			}
		} else {
			this.toggleAnnouncementBar(false);
		}

		this.currentScrollTop = scrollTop;
	}

	// 下面两个只在状态真正翻转时才碰 DOM
	toggleAnnouncementBar(hidden) {
		if (this.announcementHidden === hidden) return;

		this.announcementHidden = hidden;
		this.header.classList.toggle(this.modifiers.hiddenAnnouncement, hidden);
	}

	toggleBars(hidden) {
		if (this.barsHidden === hidden) return;

		this.barsHidden = hidden;
		this.header.classList.toggle(this.modifiers.hiddenTopBar, hidden);
		document.body.classList.toggle(this.modifiers.isHeaderSimplified, hidden);
	}
}

customElements.define('sticky-header', StickyHeader);
